import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { Alert, Stepper } from '@ds/feedback';
import { whatsappFormatado } from '@/comuns/documento';
import { PARAM_DESTINO, destinoSeguro } from '@/router/guardas';
import { SURFACES } from '@/router/rotas';
import { autocadastro } from '@/session/autocadastro';
import type { PlanoDoCadastro } from '@/session/autocadastro';
import { atual, contaSemClinica, recarregarSessao, sair, surfacePermitida } from '@/session/sessao';
import { EtapaClinica } from './EtapaClinica';
import type { DadosDaClinica } from './EtapaClinica';
import { EtapaConta } from './EtapaConta';
import { EtapaPagamento } from './EtapaPagamento';
import { EtapaPlano } from './EtapaPlano';

type Etapa =
    | { tipo: 'carregando' }
    | { tipo: 'conta' }
    | { tipo: 'confirmar'; email: string }
    | { tipo: 'aviso'; titulo: string; texto: string }
    | { tipo: 'clinica' }
    | { tipo: 'plano' }
    | { tipo: 'pagamento'; pendenteId: string };

const PASSOS = [{ label: 'Conta' }, { label: 'Clínica' }, { label: 'Plano' }, { label: 'Pagamento' }];
const INDICE: Partial<Record<Etapa['tipo'], number>> = { conta: 0, clinica: 1, plano: 2, pagamento: 3 };

/**
 * Criar conta — o autocadastro (0150), ligado por `VITE_AUTOCADASTRO`.
 *
 * Quatro passos: a conta (Google ou e-mail e senha), a clínica, o plano e o
 * pagamento. Quem foi convidado por uma clínica não passa dos dois primeiros
 * cliques: o vínculo sai do e-mail (0007), e quem volta do Google ou do link já
 * sendo da equipe segue direto para dentro.
 *
 * A tela não guarda o passo: ela o DESCOBRE a cada carregamento — a volta do
 * Google e a do link do e-mail são páginas novas. Quem descobre é o banco
 * (`autocadastro_situacao`), que sabe se há cadastro pendente e se o pagamento
 * já passou.
 */
export function Autocadastro() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [etapa, setEtapa] = useState<Etapa>({ tipo: 'carregando' });
    const [email, setEmail] = useState('');
    const [dados, setDados] = useState<DadosDaClinica>({ nomeClinica: '', nome: '', documento: '', whatsapp: '' });
    const [planos, setPlanos] = useState<PlanoDoCadastro[]>([]);
    const [plano, setPlano] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [enviando, setEnviando] = useState(false);

    const destinoPedido = params.get(PARAM_DESTINO);

    const irParaDentro = useCallback(() => {
        navigate(destinoSeguro(destinoPedido, SURFACES[surfacePermitida()].basePath), { replace: true });
    }, [navigate, destinoPedido]);

    const descobrir = useCallback(async () => {
        setErro(null);
        /* Voltou do Google já sendo da equipe: a sessão montou no arranque. */
        if (atual()) {
            irParaDentro();
            return;
        }
        const conta = await contaSemClinica();
        if (!conta) {
            setEtapa({ tipo: 'conta' });
            return;
        }
        setEmail(conta.email);
        if (!conta.emailConfirmado) {
            setEtapa({ tipo: 'confirmar', email: conta.email });
            return;
        }

        const [s, lista] = await Promise.all([autocadastro().situacao(), autocadastro().planos()]);
        if (s.temClinica) {
            setEtapa({
                tipo: 'aviso', titulo: 'Acesso fechado',
                texto: 'Esta conta faz parte de uma clínica que está suspensa ou cancelada. Fale com o suporte da Fórmula MED para voltar a entrar.',
            });
            return;
        }
        if (s.ehOperador) {
            setEtapa({ tipo: 'aviso', titulo: 'Conta da plataforma', texto: 'Conta de operador da plataforma não abre clínica pelo cadastro.' });
            return;
        }
        if (s.jaUsouTeste) {
            setEtapa({ tipo: 'aviso', titulo: 'Teste já usado', texto: 'Esta conta já usou o teste da Fórmula MED. Para abrir outra clínica, fale com o suporte.' });
            return;
        }

        setPlanos(lista);
        const p = s.pendente;
        setDados({
            nomeClinica: p?.nomeClinica ?? '',
            nome: p?.nome || conta.nome || '',
            documento: p?.documento ?? '',
            whatsapp: p?.whatsapp ? whatsappFormatado(p.whatsapp) : '',
        });
        setPlano(p?.plano ?? '');
        /* Cadastro pendente e ainda válido já tem clínica e plano: segue do pagamento. */
        setEtapa(p && !p.expirado ? { tipo: 'pagamento', pendenteId: p.id } : { tipo: 'clinica' });
    }, [irParaDentro]);

    useEffect(() => {
        descobrir().catch((e: unknown) => {
            setEtapa({ tipo: 'aviso', titulo: 'Não foi possível continuar', texto: e instanceof Error ? e.message : 'Tente de novo em instantes.' });
        });
    }, [descobrir]);

    const prepararCadastro = async () => {
        setErro(null);
        setEnviando(true);
        const r = await autocadastro().preparar({ ...dados, plano });
        setEnviando(false);
        if (!r.ok) {
            setErro(r.erro);
            return;
        }
        setEtapa({ tipo: 'pagamento', pendenteId: r.pendenteId });
    };

    /* A clínica abriu: o login que já existia agora tem vínculo, e a sessão é relida. */
    const concluir = useCallback(async () => {
        if (await recarregarSessao()) {
            irParaDentro();
            return;
        }
        setEtapa({ tipo: 'aviso', titulo: 'Clínica aberta', texto: 'A clínica foi aberta, mas a sessão não carregou. Entre de novo com a mesma conta.' });
    }, [irParaDentro]);

    const trocarDeConta = async () => {
        await sair();
        setErro(null);
        setEtapa({ tipo: 'conta' });
    };

    const volta = `${window.location.origin}/criar-conta${destinoPedido ? `?${PARAM_DESTINO}=${encodeURIComponent(destinoPedido)}` : ''}`;
    const indice = INDICE[etapa.tipo];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
                <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>Criar conta</h1>
                {etapa.tipo === 'conta' && (
                    <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                        Abra a sua clínica na Fórmula MED. No cartão, os primeiros 14 dias são grátis.
                    </p>
                )}
            </div>

            {indice !== undefined && <Stepper steps={PASSOS} current={indice} />}

            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

            {etapa.tipo === 'carregando' && (
                <p role="status" style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Carregando o cadastro…</p>
            )}

            {etapa.tipo === 'conta' && (
                <EtapaConta
                    destino={volta}
                    aoCriar={(criado, precisaConfirmar) => {
                        if (precisaConfirmar) setEtapa({ tipo: 'confirmar', email: criado });
                        else void descobrir();
                    }}
                />
            )}

            {etapa.tipo === 'confirmar' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, color: 'var(--text-primary)' }}>Confirme o e-mail</h2>
                    <Alert tone="success" dismissible={false} title="Conta criada">
                        Enviamos um link para <strong>{etapa.email}</strong>. Abra o link para continuar o cadastro da clínica.
                    </Alert>
                    <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                        Se você foi convidado por uma clínica com este e-mail, o link leva direto para dentro dela.
                    </p>
                </div>
            )}

            {etapa.tipo === 'aviso' && (
                <Alert tone="warning" dismissible={false} title={etapa.titulo}>{etapa.texto}</Alert>
            )}

            {etapa.tipo === 'clinica' && (
                <EtapaClinica
                    email={email}
                    inicial={dados}
                    aoTrocarDeConta={() => { void trocarDeConta(); }}
                    aoContinuar={(d) => { setDados(d); setErro(null); setEtapa({ tipo: 'plano' }); }}
                />
            )}

            {etapa.tipo === 'plano' && (
                <EtapaPlano
                    planos={planos}
                    escolhido={plano}
                    enviando={enviando}
                    aoEscolher={setPlano}
                    aoVoltar={() => { setErro(null); setEtapa({ tipo: 'clinica' }); }}
                    aoContinuar={() => { void prepararCadastro(); }}
                />
            )}

            {etapa.tipo === 'pagamento' && (
                <EtapaPagamento
                    pendenteId={etapa.pendenteId}
                    plano={planos.find((p) => p.codigo === plano) ?? null}
                    aoConcluir={concluir}
                    aoVoltar={() => { setErro(null); setEtapa({ tipo: 'plano' }); }}
                />
            )}
        </div>
    );
}
